import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import type {
  ClassificationResponse,
  CpcCode,
  SuggestionResponse,
} from "@patent-studio/api-types";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { Button, buttonChrome } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Panel } from "@/components/ui/Panel";
import { useAuth } from "@/contexts/AuthContext";
import {
  useChooseClassification,
  useClassification,
  useRequestClassificationRun,
} from "@/hooks/use-classification";
import { plural } from "@/lib/plural";
import {
  CodeGroup,
  CodeRow,
  ConfirmedCard,
  RunningSteps,
  SchemeSearch,
} from "../intake/ClassificationParts";
import { MaybeLink } from "../layout/MaybeLink";
import { StageFooter } from "../layout/StageFooter";
import { StagePage } from "../layout/StagePage";
import { destinationAfter, subStepPath } from "../stage-vocabulary";

/** How many of the other suggestions show before "Show N more". */
export const OTHERS_SHOWN = 3;

const LEDE =
  "Praxis suggests Cooperative Patent Classification (CPC) codes from the invention disclosure. Knowing which codes the patent is likely to fall under helps Praxis draft it. A best guess is enough, as the examiner assigns the final codes. If you are not sure which codes apply, confirm the ones Praxis has preselected.";

/**
 * Intake · Classification: the CPC codes Praxis suggests from the disclosure,
 * the attorney's choice of them, and its confirmation.
 *
 * The suggestions are asked for once per disclosure: on arrival, when none came
 * from the disclosure as it now stands and no run is in flight. Continue to
 * Figures is open only while the confirmation stands.
 */
export function ClassificationStagePane() {
  const { taskId = "" } = useParams<{ taskId: string }>();
  const { data: reading } = useClassification(taskId);
  const askForCodes = useRequestClassificationRun(taskId);
  const askedRef = useRef(false);
  const hasDisclosure = (reading?.read_files.length ?? 0) > 0;
  const running = reading?.run.status === "running";

  useEffect(() => {
    if (reading === undefined || askedRef.current || !hasDisclosure) return;
    if (running || reading.run.from_this_disclosure) return;
    askedRef.current = true;
    askForCodes.mutate();
  }, [reading, hasDisclosure, running, askForCodes]);

  return (
    <StagePage
      guide="intake"
      testId="classification-stage-pane"
      taskId={taskId}
      eyebrow="Intake · Classification"
      title="USPTO Classification"
      lede={LEDE}
    >
      {reading !== undefined && <SourceLine reading={reading} />}
      {reading === undefined ? null : !hasDisclosure ? (
        <EmptyState className="mt-6 p-6 text-[13px]">
          Classification runs once the invention disclosure is uploaded.{" "}
          <MaybeLink
            to={subStepPath(taskId, "intake", "materials")}
            className={buttonChrome({ variant: "link" })}
          >
            Go to Upload Materials
          </MaybeLink>
        </EmptyState>
      ) : running ? (
        <RunningSteps rerun={reading.confirmation !== null} />
      ) : (
        <>
          <RunNotices
            reading={reading}
            askFailure={askForCodes.isError ? askForCodes.error : null}
            onRetry={() => {
              askForCodes.mutate();
            }}
          />
          <ClassificationChoice
            key={reading.version}
            taskId={taskId}
            reading={reading}
          />
        </>
      )}
    </StagePage>
  );
}

/** What the codes were read from and the prior art left out. */
function SourceLine({ reading }: { reading: ClassificationResponse }) {
  return (
    <p className="mt-3.5 flex flex-wrap items-center gap-2 text-[12px]">
      {reading.read_files.length > 0 && (
        <span>Read: {reading.read_files.join(", ")}</span>
      )}
      {reading.prior_art_files > 0 && (
        <span className="text-muted-foreground">
          Not used: prior art ({plural(reading.prior_art_files, "file")})
        </span>
      )}
    </p>
  );
}

/** The codes the page starts selected: the attorney's stored choice, or what Praxis preselected. */
function startingChoice(reading: ClassificationResponse): string[] {
  if (reading.selected.length > 0) return reading.selected.map((c) => c.symbol);
  return reading.suggestions.filter((s) => s.preselected).map((s) => s.symbol);
}

/** The codes the attorney has added from the scheme, the stored ones first. */
function codesFromScheme(
  reading: ClassificationResponse,
  added: readonly CpcCode[],
): CpcCode[] {
  const suggested = new Set(reading.suggestions.map((s) => s.symbol));
  const stored = reading.selected.filter((c) => !suggested.has(c.symbol));
  const fresh = added.filter(
    (c) =>
      !suggested.has(c.symbol) && !stored.some((s) => s.symbol === c.symbol),
  );
  return [...stored, ...fresh];
}

/**
 * The attorney's choice: the page's own until confirmed, then stored whole with
 * its confirmation under the version read. Keyed on that version, so a reading
 * that moves starts the choice again from what it stores.
 */
function ClassificationChoice({
  taskId,
  reading,
}: {
  taskId: string;
  reading: ClassificationResponse;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const choose = useChooseClassification(taskId);
  const [chosen, setChosen] = useState<string[]>(() => startingChoice(reading));
  const [added, setAdded] = useState<CpcCode[]>([]);
  const [changing, setChanging] = useState(false);
  const selection = useMemo(() => new Set(chosen), [chosen]);
  const fromScheme = codesFromScheme(reading, added);
  const confirmed = reading.standing === "confirmed" && !changing;

  const toggle = (symbol: string) => {
    setChosen((list) =>
      list.includes(symbol)
        ? list.filter((s) => s !== symbol)
        : [...list, symbol],
    );
  };
  const confirm = () => {
    const shown = [...reading.suggestions, ...fromScheme].map((c) => c.symbol);
    const selected = shown.filter((symbol) => selection.has(symbol));
    choose.mutate(
      { selected, confirm: true, version: reading.version },
      {
        onSuccess: (stored) => {
          toast.success(
            `${plural(stored.selected.length, "classification")} confirmed.`,
          );
        },
      },
    );
  };

  return (
    <>
      {confirmed ? (
        <ConfirmedView
          reading={reading}
          byYou={user?.userId === reading.confirmation?.by}
          onChange={() => {
            setChanging(true);
          }}
        />
      ) : (
        <ReviewGroups
          reading={reading}
          selection={selection}
          fromScheme={fromScheme}
          onToggle={toggle}
          onAdd={(code) => {
            setAdded((list) =>
              list.some((c) => c.symbol === code.symbol)
                ? list
                : [...list, code],
            );
            toggle(code.symbol);
          }}
        />
      )}
      <ChoiceFooter
        confirmed={confirmed}
        count={selection.size}
        stored={reading.selected.length}
        pending={choose.isPending}
        onConfirm={confirm}
        onContinue={() => {
          const next = destinationAfter(taskId, "intake");
          if (next !== undefined) void navigate(next);
        }}
      />
    </>
  );
}

/** The confirmation as stored, the way back to changing it, and the codes it confirmed. */
function ConfirmedView({
  reading,
  byYou,
  onChange,
}: {
  reading: ClassificationResponse;
  byYou: boolean;
  onChange: () => void;
}) {
  return (
    <>
      <ConfirmedCard reading={reading} byYou={byYou} />
      <Button
        variant="tertiary"
        size="dense"
        className="mt-2"
        onClick={onChange}
      >
        Change
      </Button>
      <CodeGroup title="Confirmed">
        {reading.selected.map((code) => (
          <CodeRow key={code.symbol} code={code} checked readOnly />
        ))}
      </CodeGroup>
    </>
  );
}

/** Why the codes are not as they were: a run that failed, or a disclosure changed since the yes. */
function RunNotices({
  reading,
  askFailure,
  onRetry,
}: {
  reading: ClassificationResponse;
  /** Why the ask for codes was refused before a run was recorded, or null. */
  askFailure: unknown;
  onRetry: () => void;
}) {
  return (
    <>
      {askFailure !== null && (
        <LoadFailure
          error={askFailure}
          fallback="The codes could not be asked for."
          shape="card"
          className="mt-6"
          onRetry={onRetry}
        />
      )}
      {reading.run.status === "failed" && reading.run.from_this_disclosure && (
        <Panel
          shape="card"
          pad="snug"
          tone="destructive"
          className="mt-6 text-[13px]"
        >
          {reading.run.error ?? "The codes could not be suggested."}{" "}
          <Button variant="link" onClick={onRetry}>
            Try again
          </Button>
        </Panel>
      )}
      {reading.standing === "lapsed" && (
        <Panel
          shape="card"
          pad="snug"
          tone="warning"
          className="mt-6 text-[13px]"
        >
          <strong>Reconfirm the classification.</strong> The invention
          disclosure changed after you confirmed, so Praxis re-ran the analysis.
          Your previous selection is kept below.
        </Panel>
      )}
    </>
  );
}

/** The suggestions, preselected and the rest, the codes added from the scheme, and the search. */
function ReviewGroups({
  reading,
  selection,
  fromScheme,
  onToggle,
  onAdd,
}: {
  reading: ClassificationResponse;
  selection: ReadonlySet<string>;
  fromScheme: readonly CpcCode[];
  onToggle: (symbol: string) => void;
  onAdd: (code: CpcCode) => void;
}) {
  const threshold = reading.preselect_threshold;
  const preselected = reading.suggestions.filter((s) => s.preselected);
  const others = reading.suggestions
    .filter((s) => !s.preselected)
    .sort((a, b) => b.score - a.score);
  const row = (suggestion: SuggestionResponse) => (
    <CodeRow
      key={suggestion.symbol}
      code={suggestion}
      source={{ kind: "suggested", suggestion, threshold }}
      checked={selection.has(suggestion.symbol)}
      onToggle={() => {
        onToggle(suggestion.symbol);
      }}
    />
  );
  return (
    <>
      <CodeGroup title="Preselected">
        {preselected.length > 0
          ? preselected.map(row)
          : // Only a run that finished can say no suggestion reached the threshold.
            reading.run.status === "complete" && (
              <EmptyState className="p-4 text-[12.5px]">
                No suggestion reached the {threshold}% threshold, so nothing is
                preselected. Select the classifications that apply from the list
                below or search the full scheme.
              </EmptyState>
            )}
      </CodeGroup>
      {others.length > 0 && <OtherSuggestions others={others} row={row} />}
      {fromScheme.length > 0 && (
        <CodeGroup title="Added from the scheme">
          {fromScheme.map((code) => (
            <CodeRow
              key={code.symbol}
              code={code}
              source={{ kind: "added" }}
              checked={selection.has(code.symbol)}
              onToggle={() => {
                onToggle(code.symbol);
              }}
            />
          ))}
        </CodeGroup>
      )}
      <SchemeSearch chosen={selection} onAdd={onAdd} />
    </>
  );
}

/** The suggestions under the threshold, highest first: three, then the rest on request. */
function OtherSuggestions({
  others,
  row,
}: {
  others: readonly SuggestionResponse[];
  row: (suggestion: SuggestionResponse) => React.ReactNode;
}) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? others : others.slice(0, OTHERS_SHOWN);
  return (
    <CodeGroup title="Other suggestions">
      {shown.map(row)}
      {others.length > OTHERS_SHOWN && (
        <Button
          variant="quiet"
          size="dense"
          onClick={() => {
            setShowAll((all) => !all);
          }}
        >
          {showAll
            ? "Show fewer"
            : `Show ${String(others.length - OTHERS_SHOWN)} more`}
        </Button>
      )}
    </CodeGroup>
  );
}

/** The gate at the foot: Confirm until the choice stands, then Continue to Figures. */
function ChoiceFooter({
  confirmed,
  count,
  stored,
  pending,
  onConfirm,
  onContinue,
}: {
  confirmed: boolean;
  count: number;
  stored: number;
  pending: boolean;
  onConfirm: () => void;
  onContinue: () => void;
}) {
  const none = count === 0;
  return (
    <StageFooter
      testId="classification-footer"
      sticky
      heading={
        confirmed
          ? "Classification confirmed"
          : none
            ? "Select at least one classification"
            : `${plural(count, "classification")} selected`
      }
      detail={
        confirmed
          ? `${plural(stored, "classification")} set for this draft.`
          : none
            ? "Preselection alone does not count. Select and confirm the classifications for this draft."
            : "Confirm the selection to continue. Preselected codes still need your confirmation."
      }
      label={confirmed ? "Continue to Figures" : "Confirm Classifications"}
      pendingLabel="Confirming…"
      ready={confirmed || (!none && !pending)}
      onSubmit={confirmed ? onContinue : onConfirm}
    />
  );
}
